import type { ITableLikeTreeFilterItem } from 'components/TableLikeTree/context';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';

import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimUpdateWorkOrderChangeSeasonsGql } from '../bimWorkOrderChangeStructureQueries';

const updateWorkOrderSeasonsRequestId = 'bimWorkOrderChangeSeasonsUpdate';

type IBimSeasonValidationMessages = {
    metadata: { contractObjectId?: number; workOrderObjectId?: number };
    message: string;
};

export const useBimUpdateWorkOrderChangeSeasons = ({
    workOrderChangeId,
    updateSeasonRange,
}: {
    workOrderChangeId: number | undefined;
    updateSeasonRange: () => void;
}) => {
    const dispatch = useDispatch();

    const [validationMessages, setValidationMessages] = useState<IBimSeasonValidationMessages[]>([]);

    const [bimUpdateSeasonDataReset] = useResetStoreData({ requestId: updateWorkOrderSeasonsRequestId });

    const reset = useCallback(() => {
        bimUpdateSeasonDataReset(undefined);
    }, [bimUpdateSeasonDataReset]);

    const [updateWorkOrderSeasonsData, updateWorkOrderSeasonsStatus, , updateWorkOrderSeasonsMakeRequest] = useRequest({
        requestId: updateWorkOrderSeasonsRequestId,
        query: bimUpdateWorkOrderChangeSeasonsGql,
        withEffect: false,
        dependenciesUpdater: updateSeasonRange,
    });

    const updateSeasonsHandler = useCallback(
        (seasonsData: { lastSeason: ITableLikeTreeFilterItem | undefined }) => {
            if (workOrderChangeId) {
                setValidationMessages([]);
                dispatch(
                    updateWorkOrderSeasonsMakeRequest({
                        workOrderChangeId,
                        contractSeasonId: seasonsData?.lastSeason?.id,
                    }),
                );
            }
        },
        [dispatch, updateWorkOrderSeasonsMakeRequest, workOrderChangeId],
    );

    useEffect(() => {
        if (updateWorkOrderSeasonsData?.bimUpdateWorkOrderChangeSeasons?.validation?.length) {
            setValidationMessages(
                updateWorkOrderSeasonsData?.bimUpdateWorkOrderChangeSeasons?.validation?.map((v) => {
                    let metadata: { contractObjectId?: number; workOrderObjectId?: number };

                    try {
                        metadata = JSON.parse(v.metadata || '');
                    } catch {
                        metadata = {};
                    }

                    return { message: v.message, metadata };
                }) || [],
            );
        }
    }, [updateSeasonRange, updateWorkOrderSeasonsData?.bimUpdateWorkOrderChangeSeasons?.validation]);

    useEffect(() => {
        reset();
    }, [reset]);

    return { updateSeasonsHandler, updateSeasonsValidationMessages: validationMessages, updateWorkOrderSeasonsStatus };
};
